import { useFormik } from 'formik';
import React, { FC, useEffect } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';

import ProsperCardSVG from '../../assets/prosper-card-with-name.svg';
import { MOBILE_BREAKPOINT, colors } from '../constants';
import { getMarketingOutbound } from '../data/queries';
import { heapTrackOfferCodeSubmitted } from '../utilities/logging';

import { SpinnerButton } from './Button';
import { ModalWrapper, ModalProps } from './ModalWrapper';
import { TextInput } from './TextInput';

// Types --------------------------------------------------------------------------------------
export type OfferCodePrepopFields = {
  firstName?: string;
  lastName?: string;
  mailingAddressStreet?: string;
  mailingAddressCity?: string;
  mailingAddressState?: string;
  mailingAddressZipCode5?: string;
};

interface OfferCodeModalProps extends ModalProps {
  forceHideModal: boolean;
  passedOfferCode: string;
  onSuccess: (prepopFields: OfferCodePrepopFields, offerCode: string) => void;
}

// Styles ----------------------------------------------------------------------------------------
const OfferCodeModalContent = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  border-radius: 0.5rem;
  width: 32.125rem;
  max-width: 100%;
  background-image: url('assets/modal-gradient.png');
  background-repeat: no-repeat;
  background-size: 108% 6.5rem;
  padding-top: 10.75rem;
  padding-bottom: 2.3125rem;
  margin: 0.75rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0;
    padding-bottom: 3.125rem;
  }
  @media (max-height: ${MOBILE_BREAKPOINT}) {
    width: 33.5rem;
  }
`;

const OfferCodeModalProsperCardImage = styled(ProsperCardSVG)`
  position: absolute;
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  top: 4.5rem;
  width: 6.3125rem;
  height: auto;
  border-radius: 0.5rem;
  box-shadow: -0.125rem 0.3125rem 0.75rem rgba(0, 0, 0, 0.1);
`;

const OfferCodeModalTextContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 75%;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 93%;
  }
`;

const OfferCodeModalTitle = styled.h1`
  margin: 0 0 1rem;
  font-size: 2rem;
  font-weight: bold;
  line-height: 2.5rem;
  color: ${colors.TEXT_INPUT};
`;

const OfferCodeModalSubtitle = styled.p`
  font-size: 1rem;
  color: ${colors.TEXT_PRIMARY};
  margin-bottom: 2.1875rem;
`;

const OfferCodeModalForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-bottom: 2.5rem;
`;

const OfferCodeModalSkipButton = styled.button`
  cursor: pointer;
  color: ${colors.TEXT_ANCHOR};
`;

// This is to compensate for a bug in styled-components
// If you wrap a component, styles you add get overwritten by the base component's (bug)
// so we have to wrap the button in a div to put margin around it
const SpinnerButtonMarginContainer = styled.div`
  padding-top: 2rem;
  width: 100%;
`;

// Component --------------------------------------------------------------------------------------
export const OfferCodeModal: FC<OfferCodeModalProps> = ({
  forceHideModal,
  showModal,
  setShowModal,
  passedOfferCode,
  onSuccess,
}) => {
  const {
    values,
    errors,
    isValid,
    touched,
    dirty,
    handleChange,
    handleSubmit,
    isSubmitting,
    handleBlur,
    setFieldValue,
  } = useFormik({
    initialValues: {
      offerCode: '',
    },
    validationSchema: Yup.object().shape({
      offerCode: Yup.string()
        .max(8, 'Please enter an 8 character code.')
        .min(8, 'Please enter an 8 character code.')
        .required('Please enter an 8 character code.'),
    }),
    onSubmit: async (inputValues, { setErrors }) => {
      const { offerCode } = inputValues;
      try {
        // send the offer code
        const marketingOutboundResponse = await getMarketingOutbound(offerCode);
        // if we got errors, throw the error code
        if (
          marketingOutboundResponse.errors &&
          marketingOutboundResponse.errors.length > 0 &&
          marketingOutboundResponse.errors[0].code
        ) {
          throw new Error(marketingOutboundResponse.errors[0].code);
        }
        // if we didn't get the response we expected, throw a generic error
        if (
          !marketingOutboundResponse ||
          !marketingOutboundResponse.data ||
          !marketingOutboundResponse.data.getMarketingOutbound ||
          !marketingOutboundResponse.data.getMarketingOutbound.data ||
          !marketingOutboundResponse.data.getMarketingOutbound.data[0]
        ) {
          throw new Error('Failed to fetch the marketing outbound data');
        }
        const {
          data: {
            getMarketingOutbound: { data },
          },
        } = marketingOutboundResponse;
        // if the campaign has expired, throw an error to that effect
        const { campaignIsExpired } = data[0];
        if (campaignIsExpired) {
          throw new Error('CAMPAIGN_IS_EXPIRED');
        }
        // if we got everything we expected, grab the response and return it
        const prepopFields: OfferCodePrepopFields = {
          firstName: data[0].firstName,
          lastName: data[0].lastName,
          mailingAddressStreet: data[0].mailingAddressStreet,
          mailingAddressCity: data[0].mailingAddressCity,
          mailingAddressState: data[0].mailingAddressState,
          mailingAddressZipCode5: data[0].mailingAddressZipCode5,
        };
        heapTrackOfferCodeSubmitted();
        onSuccess(prepopFields, offerCode);
      } catch (error: any) {
        // Pop modal for when landing page offer code fails (does not affect default use case)
        // Remain closed when forceHideModal is passed in as true
        if (!forceHideModal) setShowModal(true);
        /** Handle all failures */
        // if there was definitely no record
        if (
          error.message &&
          error.message === 'MARKETING_ATTRIBUTION_RECORD_NOT_FOUND'
        ) {
          // set the error to say there was no record
          setErrors({
            offerCode:
              'The code you entered is invalid. Check for typos and try again, or proceed without a code.',
          });
        } else if (error.message && error.message === 'CAMPAIGN_IS_EXPIRED') {
          // if the campaign has expired, show that error message
          setErrors({
            offerCode:
              'The code you entered has expired, but you can proceed without it.',
          });
        } else {
          // otherwise, we don't know what went wrong, set a generic error message
          setErrors({
            offerCode:
              'Something went wrong. Check for typos and try again, or proceed without a code.',
          });
        }
      }
    },
  });

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (passedOfferCode) {
      setFieldValue('offerCode', passedOfferCode.toUpperCase());
      // Requires a short delay before submitting because of Formik validation
      // see https://github.com/jaredpalmer/formik/issues/529
      timer = setTimeout(() => {
        handleSubmit();
      }, 100);
    }
    return () => clearTimeout(timer);
  }, [passedOfferCode]);

  // called for every change within the offer code input
  const handleOfferCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // uppercase all letters
    e.target.value = e.target.value.toUpperCase();
    // pass the result through to the formik handler
    handleChange(e);
  };

  // render ---------------------------------------------------------------------------------------
  return (
    <ModalWrapper
      showModal={showModal}
      setShowModal={setShowModal}
      borderRadius="1rem"
      emptyContent
      id="offer-code-modal"
    >
      <OfferCodeModalContent>
        <OfferCodeModalProsperCardImage />
        <OfferCodeModalTextContent>
          <OfferCodeModalTitle>Access your offer</OfferCodeModalTitle>
          <OfferCodeModalSubtitle>
            Enter your 8-character offer code
          </OfferCodeModalSubtitle>
          <OfferCodeModalForm
            id="npc-offer-code-modal-form"
            onSubmit={handleSubmit}
          >
            <TextInput
              label="Offer code"
              onChange={handleOfferCodeChange}
              name="offerCode"
              value={values.offerCode}
              error={errors.offerCode}
              touched={touched.offerCode}
              maxLength={8}
              onBlur={handleBlur}
            />
            <SpinnerButtonMarginContainer>
              <SpinnerButton
                id="offer-code-modal-submit-button"
                aria-label="Submit offer code"
                type="submit"
                disabled={!dirty || !isValid || isSubmitting}
                buttonText="Continue"
                submitting={isSubmitting}
              />
            </SpinnerButtonMarginContainer>
          </OfferCodeModalForm>
          <OfferCodeModalSkipButton
            id="offer-code-modal-close-button"
            onClick={() => setShowModal(false)}
          >
            Proceed without offer code
          </OfferCodeModalSkipButton>
        </OfferCodeModalTextContent>
      </OfferCodeModalContent>
    </ModalWrapper>
  );
};
